import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Link,
  useColorModeValue,
  useDisclosure,
  IconButton,
  HStack,
  VStack,
  CloseButton,
  useColorMode,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar
} from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { List, X, Sun, Moon, User } from '@phosphor-icons/react'
import { useSelector } from 'react-redux'
import { useAuth } from '../../hooks/useAuth'
import '../../styles/mobile-navigation.css'

const Links = [
  { name: 'Home', to: '/' },
  { name: 'Search', to: '/search' },
  { name: 'Map', to: '/map' },
  { name: 'Timeline', to: '/timeline' },
  { name: 'About', to: '/about' },
  { name: 'Contact', to: '/contact' }
]

export default function Navbar() {
  const { isOpen, onToggle, onClose } = useDisclosure()
  const { colorMode, toggleColorMode } = useColorMode()
  const { signOutUser } = useAuth()
  const navigate = useNavigate()
  const user = useSelector((state) => state.auth.user)

  // Use semantic tokens from the theme
  // Note: bgColor, textColor, borderColor are applied directly via props below
  const headingColor = useColorModeValue('brand.primary', 'brand.primary'); // Use Gold for logo
  const linkColor = useColorModeValue('text.muted', 'text.muted');
  const linkHoverColor = useColorModeValue('brand.primary', 'brand.primary'); // Hover to Gold

  const handleSignOut = async () => {
    try {
      await signOutUser()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <Box>
      <Flex
        bg="bg.surface" // Use theme surface background
        color="text.default" // Use theme default text color
        h={'44px'}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor="border.default" // Use theme default border color
        align={'center'}
        position="sticky"
        top={0}
        zIndex={1000}
        _before={{ // Conditionally apply texture
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: useColorModeValue( // Only set image if enabled
            'url("/textures/linen.png")',
            'url("/textures/leather.png")'
          ), // Set to none if disabled
          backgroundSize: 'cover', // Or 'auto' if tiling is preferred
          backgroundPosition: 'center',
          opacity: 0.1, // Set opacity to 0 if disabled
          pointerEvents: 'none',
          zIndex: -1, // Place behind content
          transition: 'opacity 0.3s ease-in-out', // Add transition
        }}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          display={{ base: 'flex', md: 'none' }}
          align="center"
        >
            <IconButton 
              onClick={onToggle} 
              icon={<List size={20} />} 
              variant={'ghost'} 
              aria-label={'Toggle Navigation'}
              size="sm"
            />
        </Flex>

        <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }} align="center">
          <Flex align="center">
            <Text
              as={RouterLink}
              to="/"
              textAlign={{ base: 'center', md: 'left' }}
              fontFamily={'heading'}
              color={linkColor} // Changed from headingColor to linkColor
              fontWeight="bold"
              fontSize="md"
              lineHeight="1"
              display="flex"
              alignItems="center"
            >
              Megalith Maps
            </Text>
          </Flex>

          <Flex display={{ base: 'none', md: 'flex' }} ml={8} align="center">
            <Stack direction={'row'} spacing={6} align="center">
              {Links.map((link) => (
                <Link
                  key={link.name}
                  as={RouterLink}
                  to={link.to}
                  px={2}
                  fontSize={'sm'}
                  fontWeight={500}
                  color={linkColor} // Use themed link color
                  lineHeight="1"
                  display="flex"
                  alignItems="center"
                  height="44px"
                  _hover={{
                    textDecoration: 'none',
                    color: linkHoverColor, // Use themed hover color (Gold)
                  }}
                >
                  {link.name}
                </Link>
              ))}
              {user && (
                <>
                  <Link
                    as={RouterLink}
                    to="/dashboard"
                    px={2}
                    fontSize={'sm'}
                    fontWeight={500}
                    color={linkColor} // Use themed link color
                    lineHeight="1"
                    display="flex"
                    alignItems="center"
                    height="44px"
                    _hover={{
                      textDecoration: 'none',
                      color: linkHoverColor, // Use themed hover color (Gold)
                    }}
                  >
                    Dashboard
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      as={RouterLink}
                      to="/admin"
                      px={2}
                      fontSize={'sm'}
                      fontWeight={500}
                      color={linkColor} // Use themed link color
                      lineHeight="1"
                      display="flex"
                      alignItems="center"
                      height="44px"
                      _hover={{
                        textDecoration: 'none',
                        color: linkHoverColor, // Use themed hover color (Gold)
                      }}
                    >
                      Admin
                    </Link>
                  )}
                </>
              )}
            </Stack>
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={4}
          align="center"
        >
          <IconButton
            aria-label="Toggle color mode"
            icon={colorMode === 'light' ? <Moon size={14} /> : <Sun size={14} />}
            onClick={toggleColorMode}
            variant="ghost"
            size="xs"
            h="24px"
            w="24px"
            minH="24px"
            minW="24px"
          />
          
          {user ? (
            <Menu>
              <MenuButton
                as={Button}
                rounded={'full'}
                variant={'link'}
                cursor={'pointer'}
                minW={0}
              >
                  <Avatar size={'sm'} name={user?.displayName || 'User'} src={user?.photoURL || ''} />
              </MenuButton>
              <MenuList>
                <MenuItem as={RouterLink} to="/dashboard">Dashboard</MenuItem>
                {user?.role === 'admin' && (
                  <MenuItem as={RouterLink} to="/admin">Admin</MenuItem>
                )}
                <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
              </MenuList>
            </Menu>
          ) : (
            <>
              <Button
                as={RouterLink}
                to="/login"
                fontSize={'sm'}
                fontWeight={400}
                variant={'link'}
                display={{ base: 'none', md: 'inline-flex' }}
              >
                Sign In
              </Button>
              <Button
                as={RouterLink}
                to="/signup"
                display={{ base: 'none', md: 'inline-flex' }}
                fontSize={'xs'}
                fontWeight={500}
                variant={'outline'}
                borderColor={linkColor}
                color={linkColor}
                px={2}
                py={0}
                h="24px"
                minH="24px"
                borderWidth="1px"
                _hover={{
                  bg: 'bg.accent',
                  color: linkHoverColor,
                  borderColor: linkHoverColor,
                }}
              >
                Sign Up
              </Button>
            </>
          )}
        </Stack>
      </Flex>

      {/* Enhanced Mobile menu */}
      <Box
        display={{ md: 'none' }}
        pos="fixed"
        top={0}
        left={0}
        bottom={0}
        w="full"
        maxW="sm"
        bg="bg.surface"
        transform={isOpen ? 'translateX(0)' : 'translateX(-100%)'}
        transition="transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        zIndex={isOpen ? 1001 : -1}
        boxShadow={isOpen ? "2xl" : "none"}
        borderRight="1px solid"
        borderColor="border.default"
        overflow="hidden"
        visibility={isOpen ? 'visible' : 'hidden'}
        opacity={isOpen ? 1 : 0}
        pointerEvents={isOpen ? 'auto' : 'none'}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: useColorModeValue(
            'url("/textures/aged-paper.png")',
            'url("/textures/leather.png")'
          ),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.05,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        {/* Header Section */}
        <Box
          p={6}
          borderBottom="1px solid"
          borderColor="border.light"
          bg="bg.accent"
          position="relative"
        >
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                fontFamily="heading"
                color="text.default"
                letterSpacing="tight"
              >
                Megalith Maps
              </Text>
              <Text
                fontSize="sm"
                color="text.muted"
                fontFamily="body"
                fontStyle="italic"
              >
                Explorer's Journal
              </Text>
            </VStack>
            <IconButton
              icon={<X size={24} />}
              onClick={onClose}
              variant="ghost"
              size="lg"
              color="text.muted"
              _hover={{
                color: "text.default",
                bg: "whiteAlpha.200"
              }}
              aria-label="Close menu"
            />
          </Flex>
        </Box>

        {/* User Section */}
        {user && (
          <Box
            p={4}
            borderBottom="1px solid"
            borderColor="border.light"
            bg="bg.default"
          >
            <HStack spacing={3}>
              <Avatar 
                size="md" 
                name={user?.displayName || 'User'} 
                src={user?.photoURL || ''} 
              />
              <VStack align="start" spacing={0}>
                <Text fontSize="md" fontWeight="semibold" color="text.default">
                  {user?.displayName || 'Explorer'}
                </Text>
                <Text fontSize="sm" color="text.muted">
                  {user?.email}
                </Text>
              </VStack>
            </HStack>
          </Box>
        )}

        {/* Navigation Content */}
        <Box flex="1" overflowY="auto" p={6} className="mobile-menu-scroll">
          <VStack spacing={6} align="stretch">
            
            {/* Main Navigation Section */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="text.muted"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={3}
                fontFamily="heading"
              >
                Explore
              </Text>
              <VStack spacing={2} align="stretch">
                {Links.map((link, index) => (
                  <Button
                    key={link.name}
                    as={RouterLink}
                    to={link.to}
                    variant="journal"
                    size="lg"
                    justifyContent="flex-start"
                    fontWeight="medium"
                    fontSize="md"
                    color="text.default"
                    bg="transparent"
                    border="1px solid"
                    borderColor="transparent"
                    _hover={{
                      bg: "bg.accent",
                      borderColor: "border.default",
                      color: "brand.primary",
                      transform: "translateX(4px)"
                    }}
                    _active={{
                      transform: "translateX(2px)"
                    }}
                    transition="all 0.2s ease-out"
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: isOpen ? 'slideInLeft 0.3s ease-out forwards' : 'none'
                    }}
                    onClick={onClose}
                  >
                    {link.name}
                  </Button>
                ))}
              </VStack>
            </Box>

            {/* User Actions Section */}
            {user && (
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="text.muted"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={3}
                  fontFamily="heading"
                >
                  Your Account
                </Text>
                <VStack spacing={2} align="stretch">
                  <Button
                    as={RouterLink}
                    to="/dashboard"
                    variant="journal"
                    size="lg"
                    justifyContent="flex-start"
                    fontWeight="medium"
                    fontSize="md"
                    color="text.default"
                    bg="transparent"
                    border="1px solid"
                    borderColor="transparent"
                    _hover={{
                      bg: "bg.accent",
                      borderColor: "border.default",
                      color: "brand.primary",
                      transform: "translateX(4px)"
                    }}
                    onClick={onClose}
                  >
                    Dashboard
                  </Button>
                  {user?.role === 'admin' && (
                    <Button
                      as={RouterLink}
                      to="/admin"
                      variant="journal"
                      size="lg"
                      justifyContent="flex-start"
                      fontWeight="medium"
                      fontSize="md"
                      color="text.default"
                      bg="transparent"
                      border="1px solid"
                      borderColor="transparent"
                      _hover={{
                        bg: "bg.accent",
                        borderColor: "border.default",
                        color: "brand.primary",
                        transform: "translateX(4px)"
                      }}
                      onClick={onClose}
                    >
                      Admin Panel
                    </Button>
                  )}
                </VStack>
              </Box>
            )}

            {/* Settings Section */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="text.muted"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={3}
                fontFamily="heading"
              >
                Settings
              </Text>
              <VStack spacing={2} align="stretch">
                <Button
                  variant="journal"
                  size="lg"
                  justifyContent="space-between"
                  fontWeight="medium"
                  fontSize="md"
                  color="text.default"
                  bg="transparent"
                  border="1px solid"
                  borderColor="transparent"
                  _hover={{
                    bg: "bg.accent",
                    borderColor: "border.default"
                  }}
                  onClick={toggleColorMode}
                  leftIcon={colorMode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                >
                  <Text flex="1" textAlign="left">
                    {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
                  </Text>
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Box>

        {/* Footer Actions */}
        <Box
          p={6}
          borderTop="1px solid"
          borderColor="border.light"
          bg="bg.accent"
        >
          {user ? (
            <Button
              w="full"
              variant="outline"
              size="lg"
              colorScheme="red"
              fontWeight="medium"
              onClick={() => {
                handleSignOut()
                onClose()
              }}
            >
              Sign Out
            </Button>
          ) : (
            <VStack spacing={3}>
              <Button
                as={RouterLink}
                to="/login"
                w="full"
                variant="outline"
                size="lg"
                fontWeight="medium"
                onClick={onClose}
              >
                Sign In
              </Button>
              <Button
                as={RouterLink}
                to="/signup"
                w="full"
                variant="primary"
                size="lg"
                fontWeight="medium"
                onClick={onClose}
              >
                Join the Adventure
              </Button>
            </VStack>
          )}
        </Box>

      </Box>

      {/* Overlay */}
      <Box
        pos="fixed"
        top={0}
        right={0}
        bottom={0}
        left={0}
        bg="blackAlpha.400"
        zIndex={isOpen ? 1000 : -1}
        visibility={isOpen ? 'visible' : 'hidden'}
        opacity={isOpen ? 1 : 0}
        transition="opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        pointerEvents={isOpen ? 'auto' : 'none'}
        onClick={onClose}
        display={{ md: 'none' }}
      />
    </Box>
  )
}
